/* eslint-disable indent */
import {
  cloneElement,
  createContext,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import { useLocation } from "react-router";
import Button from "@aleaplay/web-components/ui/Button";
import Modal from "@aleaplay/web-components/ui/Modal";

interface FormModalProps {
  children: ReactNode;
}

export interface ModalDataChildProps {
  onClose?: () => void;
  setLoading?: (bool: boolean) => void;
}

export interface ModalData {
  title: string;
  content: ReactElement<ModalDataChildProps>;
  buttons?: {
    label: string;
    onClick?: (props: ModalDataChildProps) => void;
    form?: string;
    modifier?: "primary" | "secondary";
    type?: string;
    "data-testid"?: string;
  }[];
}

interface ModalContextProps {
  setModal: (data: ModalData) => Promise<unknown>;
}

export const ModalContext = createContext({} as ModalContextProps);

export const ModalProvider = ({ children }: FormModalProps): ReactElement => {
  const [data, setData] = useState<ModalData | false>(false);
  const [loading, setLoading] = useState(false);
  const resolver = useRef<any>(null);

  const { pathname } = useLocation();

  useEffect(() => {
    if (data) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const setModal = (modalData: ModalData) => {
    setData(modalData);

    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  };

  const onClose = () => {
    resolver.current && resolver.current();
    setLoading(false);
    setData(false);
  };

  const contextValue = useMemo(() => ({ setModal }), []);

  return (
    <>
      <Modal
        title={data && data.title}
        domNode={document.querySelector("#root")}
        actions={
          data && data.buttons
            ? data.buttons.map(({ label, onClick, ...rest }) => (
              <Button
                key={label}
                modifier="primary"
                ripple
                onClick={() => onClick?.({ setLoading, onClose })}
                {...rest}
                >
                {label}
              </Button>
              ))
            : [
              <Button key="confirm" modifier="primary" ripple onClick={onClose}>
                <FormattedMessage id="label.close" />
              </Button>,
        ]
        }
        visible={Boolean(data)}
        loading={loading}
        onClose={onClose}
      >
        {data &&
          cloneElement(data.content, {
            ...data.content.props,
            onClose,
            setLoading,
          })}
      </Modal>
      <ModalContext.Provider value={contextValue}>{children}</ModalContext.Provider>
    </>
  );
};

export const useModalContext = () => useContext(ModalContext);
